import tmActions from './tmActions';
import tmMutations from './tmMutations';
import tmGetters from './tmGetters';

export default class TimemachineStoreModule {
  constructor({ SCENE_EDIT_TIMEOUT, CLICK_OUTSIDE_IGNORABLE_SELECTORS }) {
    /**
     * @TIP to access the module: mapActions('timemachine', ['...'])
     */
    this.namespaced = true;

    this.state = {
      /**
       * keychain of flags for diagnostic purposes relevant during developing
       */
      diagnosticEscort: {
        tp1: null, // test point
      },

      CONST: Object.freeze({
        /**
         * z-indexes for each stacking context
         * @link: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context
         *
         * z-index defined in array so it will be easy for tweak during development
         * will be assigned to tm-machine element
         * access from css as var(--tm-zPlayhead)
         * access from js as state.CONST.Z_ORDER.MAIN.indexOf('something')
         */
        Z_ORDER: {
          MAIN: [
            null,
            'zBeatAnchor',

            'zVisualSprite',
            'zVisualExpired',
            'zVisualBadge',
            'zVisualEmptyScene',

            'zOverlaySprite',
            'zOverlayExpired',
            'zOverlayBadge',
            'zOverlayBorder',
            'zOverlayResize',

            'zVisualStatus',
            'zVisualBorder',
            'zVisualResize',

            'zAudioShadow',

            'zGapIndicator',
            'zTrackItemMenu',
            'zOverlayMenu',
            'zPlayhead',

            'zTracksFog',
            'zMovableGhost',
            'zVisualAnchor',
            'zScrollSensor',

            'zDiagPoint',
            'zDiagTracks',
          ],
          RESERVED: [],
        },

        /**
         * tactile distance to trigger drag and drop operation in pixels
         * counted from mouse down point to other point in any direction
         */
        DND_SENSITIVITY: {
          DEFAULT: 8,
          RESIZE: 1,
          SECONDHAND: 1,
        },

        /**
         * snapping distance for different objects manipulated by mouse
         */
        AUTOSNAP_PROXIMITY: {
          DEFAULT: 15,
          RESIZE: 10,
          ASSET: 40,
          AUDIO: 10,
        },

        SCENE_EDIT_TIMEOUT,
        CLICK_OUTSIDE_IGNORABLE_SELECTORS,
      }),

      /**
       * flags for actions (multiple mods could be turned on at once)
       */
      MODE: {
        DIAGNOSTIC: !!0,
        SCENE_EDIT: false,
        ASSET_RESIZE: false,
        OVERLAY_RESIZE: false,
        VISUAL_DND: false,
        SECONDHAND_DND: false,
        OVERLAY_DND: false,
        AUDIO_DND: false,
        AUDIO_RESIZE: false,
        RCP_MSG_PLAY: false,
        VA_GAP_VISIBLE: false,
        OPTIONAL_AUDIO_INSERT: false,
        AUDIO_MULTISELECT: false,
        SECONDHAND_CUT: false,
      },

      /**
       * state machine (can be in single state at a time)
       */
      STATE: {
        INITIALIZING: true,
      },

      /**
       * set of toggable timemachine features that can be turned on/off
       */
      USE: {
        showBeats: false,
        showTimelineScenes: false,
        sceneEdit: true,
      },

      /**
       * definition of runtime dependent variables that also has an effect on css
       * for declaration of other static css variables refer to tmTimemachine.vue style
       * please bare in mind that:
       *    unit is part of the name, prefix
       *    access from css as var('--tm-pxVisualMenuW') prefix
       */
      cssVariables: {
        pxRowBlankTop: 29,
        pxRowBlankBottom: 30,
        pxAxisTopPadding: 15,
        pxAxisSidePadding: 37,
        pxAssetWidth: 150,
        pxAssetHeight: 87,
        pxTranscriptTrackH: 30,
        pxAudioTrackH: 30,
        pxResizeW: 21,
        pxVisualMenuW: 204,
        pxVisualSceneMenuW: 60,
        pxAudioDropMenuW: 370,
        pxAudioMenuW: 154,
        pxOverlayMenuW: 153,
        pxVisualSpanBorder: 4,
        pxSecondhandBannerW: 58,
        // below are combinatorial or runtime dependent vars...
        pxOverlayH: 0,
        pxOverlayTopOffset: 0,
        pxAudioTopOffset: 0,
        pxRowHeight: 0,
      },

      /**
       * timeline view container dimensions
       */
      viewOffsets: {
        axis: null,
        scrollTop: 0, // consumed outside
      },

      /**
       * time grid layer of row coordinates
       */
      timeLayer: {
        /**
         * single row duration in seconds
         */
        rowDuration: 30,
        rows: [],
      },

      /**
       * timeline treks containers and their individual total durations
       */
      trackLayer: {
        visual: [],
        visualDuration: 0,
        audio: [],
        audioDuration: 0,
      },

      /**
       * current time of video playhead
       */
      secondhand: 0,
      secondhandVisibilityTrigger: 0,

      /**
       * keychain of selected objects on timeline
       */
      selected: {
        visual: null,
        overlay: null,
        audio: null,
      },

      /**
       * keychain of flags relevant during D&D of objects
       */
      dndEscort: {
        trackInHand: null,
        isExternal: false,
        useScrollSensor: false,
        showVisualAnchors: false,
        insertionTime: null,
        track2replace: null,
        overlay2replace: null,
        audioTracksBeforePush: null,
        multiSelectIndex: null,
      },

      /**
       * keychain of flags relevant during D&D of overlays in MODE.SCENE_EDIT
       */
      sceneEscort: {
        sceneTrack: null,
        byHover: false,
        actingPipa: null,
      },
    };

    this.getters = tmGetters;
    this.mutations = tmMutations;
    this.actions = tmActions;
    this.plugins = [];
  }
}
